import React from "react";
import { flushSync } from "react-dom";



const Composer = React.memo(
  React.forwardRef(function Composer(
{
  onSend,
  disabled,
  fetchQuickReplies,
  onQuickReplySelect,
  isGroup = false,
  fetchMentionParticipants,
  inputClassName = "composer-input",
  popupClassNames = {
    container: "rr-popup",
    list: "rr-list",
    item: "rr-item",
    itemAtivo: "is-active",
    titulo: "rr-item-title",
    corpo: "rr-item-body",
  },
},
    ref
  ) {
    const [texto, setTexto] = React.useState("");
    const [rrVisivel, setRrVisivel] = React.useState(false);
    const [rrLista, setRrLista] = React.useState([]);
    const [rrIndex, setRrIndex] = React.useState(0);

    const [mentionVisivel, setMentionVisivel] = React.useState(false);
    const [mentionLista, setMentionLista] = React.useState([]);
    const [mentionIndex, setMentionIndex] = React.useState(0);
    const [mentions, setMentions] = React.useState([]);

    const areaRef = React.useRef(null);
    const rafIdRef = React.useRef(0);
    const lastActionRef = React.useRef(null);
    const restoreFocusTimerRef = React.useRef(null);

    const debRRRef = React.useRef(null);
    const debMentionRef = React.useRef(null);

    const restoreFocus = React.useCallback(() => {
      if (disabled) return;

      const el = areaRef.current;
      if (!el) return;

      el.focus({ preventScroll: true });

      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        // ignora browsers que não aceitarem
      }

      requestAnimationFrame(() => {
        const input = areaRef.current;
        if (!input || disabled) return;

        input.focus({ preventScroll: true });

        const end = input.value.length;
        try {
          input.setSelectionRange(end, end);
        } catch {
          // ignora
        }

        clearTimeout(restoreFocusTimerRef.current);
        restoreFocusTimerRef.current = setTimeout(() => {
          const finalInput = areaRef.current;
          if (!finalInput || disabled) return;

          finalInput.focus({ preventScroll: true });

          const finalEnd = finalInput.value.length;
          try {
            finalInput.setSelectionRange(finalEnd, finalEnd);
          } catch {
            // ignora
          }
        }, 0);
      });
    }, [disabled]);

    const scheduleResize = React.useCallback(() => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

      rafIdRef.current = requestAnimationFrame(() => {
        const el = areaRef.current;
        if (!el) return;

        const max = 120;
        el.style.height = "auto";

        const next = Math.min(el.scrollHeight, max);
        el.style.height = `${next}px`;
        el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
      });
    }, []);

    React.useEffect(() => {
      scheduleResize();
    }, [texto, scheduleResize]);

    React.useEffect(() => {
      const handlePaste = (e) => {
        if (!e.clipboardData) return;

        const items = e.clipboardData.items;
        if (!items || items.length === 0) return;

        for (const item of items) {
          if (item.type && item.type.includes("image")) {
            const file = item.getAsFile();
            if (!file) return;

            const customEvent = new CustomEvent("composer-paste-image", {
              detail: file,
            });

            window.dispatchEvent(customEvent);
            e.preventDefault();
            break;
          }
        }
      };

      const el = areaRef.current;
      if (el) el.addEventListener("paste", handlePaste);

      return () => {
        if (el) el.removeEventListener("paste", handlePaste);
      };
    }, []);

React.useEffect(() => {
  return () => {
    clearTimeout(debRRRef.current);
    clearTimeout(debMentionRef.current);
    clearTimeout(restoreFocusTimerRef.current);

    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
  };
}, []);

    const triggerRR = React.useCallback(
      (q) => {
clearTimeout(debRRRef.current);

debRRRef.current = setTimeout(async () => {
          try {
            if (!fetchQuickReplies) return;

            const data = await fetchQuickReplies(q || "");
            setRrLista(Array.isArray(data) ? data : []);
          } catch {
            setRrLista([]);
          }
        }, 180);
      },
      [fetchQuickReplies]
    );

    const triggerMention = React.useCallback(
  (q) => {
clearTimeout(debMentionRef.current);

debMentionRef.current = setTimeout(async () => {
      try {
        if (!fetchMentionParticipants || !isGroup) return;

        const data = await fetchMentionParticipants(q || "");
        setMentionLista(Array.isArray(data) ? data : []);
      } catch {
        setMentionLista([]);
      }
    }, 180);
  },
  [fetchMentionParticipants, isGroup]
);

const escapeRegex = (str) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const sendNow = React.useCallback(() => {
  const msgTrim = texto.trim();
  if (!msgTrim || disabled) return false;

const mentionsValidas = mentions.filter((m) =>
  new RegExp(`${escapeRegex(m.token)}(\\s|$)`).test(texto)
);

let toSend = texto;

mentionsValidas.forEach((m) => {
  const rx = new RegExp(`${escapeRegex(m.token)}(\\s|$)`, "g");
  toSend = toSend.replace(rx, `@${m.phone}$1`);
});

  lastActionRef.current = "sent";

  flushSync(() => {
    setTexto("");
    setRrVisivel(false);
    setRrLista([]);
    setRrIndex(0);

    setMentionVisivel(false);
    setMentionLista([]);
    setMentionIndex(0);
    setMentions([]);
  });

  scheduleResize();
  restoreFocus();

  onSend?.(toSend, {
    mentioned: mentionsValidas.map((m) => m.phone),
    mentions: mentionsValidas,
  });

  return true;
}, [texto, mentions, disabled, onSend, scheduleResize, restoreFocus]);

    React.useImperativeHandle(
      ref,
      () => ({
        send: () => sendNow(),
        focus: () => restoreFocus(),
        getValue: () => texto,
        setValue: (v) => {
        setTexto(v ?? "");
        setMentions([]);
        requestAnimationFrame(() => {
          scheduleResize();
          restoreFocus();
        });
      },
      }),
      [sendNow, texto, scheduleResize, restoreFocus]
    );

    React.useEffect(() => {
      if (!disabled && lastActionRef.current === "sent") {
        restoreFocus();
        scheduleResize();
        lastActionRef.current = null;
      }
    }, [disabled, restoreFocus, scheduleResize]);

const handleChange = React.useCallback(
  (e) => {
    const txt = e.target.value;
    setTexto(txt);
    scheduleResize();

    const cursor = e.target.selectionStart ?? txt.length;
    const ateCursor = txt.slice(0, cursor);

    const mentionMatch = isGroup ? ateCursor.match(/(?:^|\s)@([^\s@]*)$/) : null;
    const slashMatch = ateCursor.match(/\/([\w-]*)$/);

    if (mentionMatch) {
      triggerMention(mentionMatch[1] || "");
      setMentionVisivel(true);
      setMentionIndex(0);

      setRrVisivel(false);
      return;
    }

    if (slashMatch) {
      triggerRR(slashMatch[1] || "");
      setRrVisivel(true);
      setRrIndex(0);

      setMentionVisivel(false);
      return;
    }

    setRrVisivel(false);
    setMentionVisivel(false);
  },
  [scheduleResize, triggerRR, triggerMention, isGroup]
);

 const aplicarRespostaRapida = React.useCallback(
  async (item) => {
    if (!item) return;

    const temArquivo = !!item?.arquivo_url;

    // Se tiver arquivo, delega para o PainelMensagens
    if (temArquivo && onQuickReplySelect) {
      setRrVisivel(false);
      setRrLista([]);
      setRrIndex(0);

      await onQuickReplySelect(item);

      requestAnimationFrame(() => {
        restoreFocus();
        scheduleResize();
      });

      return;
    }

    // Se não tiver arquivo, mantém comportamento atual:
    setTexto((prev) => {
      const el = areaRef.current;
      const pos = el ? el.selectionStart ?? prev.length : prev.length;

      const antes = prev
        .slice(0, pos)
        .replace(/\/[\w-]*$/, item.conteudo || "");

      const depois = prev.slice(pos);
      const novo = `${antes}${depois}`;

      requestAnimationFrame(() => {
        const input = areaRef.current;
        if (!input) return;

        const newPos = antes.length;
        input.focus({ preventScroll: true });

        try {
          input.selectionStart = newPos;
          input.selectionEnd = newPos;
        } catch {
          // ignora
        }

        scheduleResize();
      });

      return novo;
    });

    setRrVisivel(false);
  },
  [onQuickReplySelect, restoreFocus, scheduleResize]
);

const aplicarMention = React.useCallback((item) => {
  if (!item) return;

  setTexto((prev) => {
    const el = areaRef.current;
    const pos = el ? el.selectionStart ?? prev.length : prev.length;

    const antesCursor = prev.slice(0, pos);
    const depoisCursor = prev.slice(pos);

    const novoAntes = antesCursor.replace(
      /(^|\s)@[^\s@]*$/,
      `$1@${item.nome || item.label || item.short || "contato"} `
    );

    const novoTexto = `${novoAntes}${depoisCursor}`;

    requestAnimationFrame(() => {
      const input = areaRef.current;
      if (!input) return;

      const newPos = novoAntes.length;
      input.focus({ preventScroll: true });

      try {
        input.selectionStart = newPos;
        input.selectionEnd = newPos;
      } catch {}

      scheduleResize();
    });

    return novoTexto;
  });

setMentions((prev) => {
  const phone = String(item.phone || item.telefone || "").replace(/\D+/g, "");
  if (!phone) return prev;

  const nomeVisivel = item.nome || item.label || item.short || phone;
  const token = `@${nomeVisivel}`;

  const jaExiste = prev.some((m) => m.phone === phone && m.token === token);
  if (jaExiste) return prev;

  return [
    ...prev,
    {
      phone,
      nome: nomeVisivel,
      token,
    },
  ];
});

  setMentionVisivel(false);
  setMentionLista([]);
  setMentionIndex(0);
}, [scheduleResize]);

  const handleKeyDown = React.useCallback(
  (e) => {
    if (mentionVisivel) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) =>
          Math.min(i + 1, Math.max(0, mentionLista.length - 1))
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex((i) => Math.max(i - 1, 0));
        return;
      }

      if (e.key === "Escape") {
        setMentionVisivel(false);
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        aplicarMention(mentionLista[mentionIndex]);
        return;
      }
    }

    if (rrVisivel) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setRrIndex((i) => Math.min(i + 1, Math.max(0, rrLista.length - 1)));
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setRrIndex((i) => Math.max(i - 1, 0));
        return;
      }

      if (e.key === "Escape") {
        setRrVisivel(false);
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        aplicarRespostaRapida(rrLista[rrIndex]);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendNow();
    }
  },
  [
    mentionVisivel,
    mentionLista,
    mentionIndex,
    aplicarMention,
    rrVisivel,
    rrLista,
    rrIndex,
    aplicarRespostaRapida,
    sendNow,
  ]
);

    return (
      <div style={{ position: "relative", width: "100%", marginTop: "10px" }}>
        <textarea
          ref={areaRef}
          className={inputClassName}
          placeholder="Digite sua msg"
          value={texto}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onInput={scheduleResize}
          rows={1}
          disabled={disabled}
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck={true}
          style={{
            width: "100%",
            resize: "none",
            minHeight: 28,
            maxHeight: 120,
            overflowY: "hidden",
            willChange: "height",
          }}
        />

        {rrVisivel && rrLista.length > 0 && (
          <div className={popupClassNames.container}>
            <div className={popupClassNames.list}>
              {rrLista.map((item, idx) => {
                const slugify = (s) =>
                  (s || "")
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .toLowerCase()
                    .replace(/[^\w]+/g, "");

                const atalho =
                  item.slug ||
                  item.atalho ||
                  item.comando ||
                  slugify(item.titulo || item.conteudo);

                const ativo = idx === rrIndex ? popupClassNames.itemAtivo : "";

                return (
                  <button
                    key={item.id ?? idx}
                    type="button"
                    className={`${popupClassNames.item} ${ativo}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      aplicarRespostaRapida(item);
                    }}
                    onMouseEnter={() => setRrIndex(idx)}
                    title={item.titulo || ""}
                  >
                    <div className={popupClassNames.titulo}>
                      <span className="rr-item-slash">{atalho}</span>
                      <span className="rr-item-dash"> — </span>
                      <span className="rr-item-strong">
                        {item.titulo || "Sem título"}
                      </span>
                    </div>

                    <div className={popupClassNames.corpo}>
                      {item.conteudo?.length > 140
                        ? `${item.conteudo.slice(0, 140)}…`
                        : item.conteudo}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {mentionVisivel && mentionLista.length > 0 && (
  <div className={popupClassNames.container}>
    <div className={popupClassNames.list}>
      {mentionLista.map((item, idx) => {
        const ativo = idx === mentionIndex ? popupClassNames.itemAtivo : "";

        return (
          <button
            key={item.id ?? item.phone ?? idx}
            type="button"
            className={`${popupClassNames.item} ${ativo}`}
            onMouseDown={(e) => {
              e.preventDefault();
              aplicarMention(item);
            }}
            onMouseEnter={() => setMentionIndex(idx)}
            title={item.nome || item.label || ""}
          >
            <div className={popupClassNames.titulo}>
              <span className="rr-item-slash">
                @{item.nome || item.label || item.short || "Sem nome"}
              </span>
            </div>

            <div className={popupClassNames.corpo}>
              {item.phone || item.telefone || ""}
            </div>
          </button>
        );
      })}
    </div>
  </div>
)}
      </div>
    );
  })
);

export default Composer;